///
/// EarthBrowser
///
/// Created by Matt Giger on 10/22/2026
/// Copyright (c) 2026 Geodata Labs
///
/// GLM lightning flashes near active storms, drawn as additive sparks over
/// the cloud deck (lightning.glsl). The flashes come from the clouds
/// dataset's "lightning" shard (weather-services storm-lightning.ts: one row
/// per GOES GLM flash inside a storm box). Each rendered frame shows the
/// flashes within cWindowMs before the scene clock, fading with age, so a
/// stepped time-lapse sparkles differently every step and real-time
/// playback flickers. Video render mode only for now (CloudsDatasetLayer
/// gates the shard download); the data path is in place for an app layer.
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { cube } from "@stovokor/math/cube"
import { trig } from "@stovokor/math/math"
import { type matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { vec2, type vec3 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { BlendFactor, DrawMode } from "@stovokor/shader/GLConstants"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"

export interface LightningFlash {
	stormId: string
	/** First event time, epoch ms */
	t: number
	lat: number
	lon: number
	/** Radiant energy, joules */
	energy: number
}

// Flashes younger than this (scene time) are shown, fading out with age.
// A 2-minute time-lapse step then shows each step's own flashes
const cWindowMs = 150_000
// Height above the ellipsoid, km: over the cloud deck the overlay draws on
// the surface, under nothing
const cAltitudeKm = 14
// Radiant energy range mapped to brightness (log10 joules): GLM flashes run
// from ~1e-15 J (faint) to 1e-12 J and up (very bright)
const cLogEnergyFaint = -15
const cLogEnergyBright = -12.5
// Quad half-size for a full-strength flash, logical pixels
const cSizePx = 22
// Cap per frame so an exceptionally active deck stays a sparkle
const cMaxFlashesPerFrame = 3000
// position (3) + corner (2) + strength (1), six vertices per flash
const cVertexFloats = 6
const cQuadCorners = [-1, -1, 1, -1, 1, 1, -1, -1, 1, 1, -1, 1]

export class StormLightningNode extends NodeBase {
	private readonly context: EarthBrowserContext
	private readonly geometry: LightningGeometry
	// Flashes sorted by time with precomputed positions
	private times: Float64Array = new Float64Array(0)
	private positions: Float32Array = new Float32Array(0)
	private strengths: Float32Array = new Float32Array(0)
	private builtForMs = Number.NaN

	constructor(context: EarthBrowserContext) {
		super()
		this.context = context
		this.geometry = new LightningGeometry(context)
		this.renderer = new LightningRenderer(context, this.geometry)
	}

	public get count(): number {
		return this.times.length
	}

	/** Flash rate (per minute) over the display window ending at t, or null with no data */
	public ratePerMinute(nowMs: number): number | null {
		if (this.times.length === 0) return null
		const inWindow = this.lowerBound(nowMs + 1) - this.lowerBound(nowMs - cWindowMs)
		return inWindow / (cWindowMs / 60_000)
	}

	/** Replace the flash set (any order); positions and energies are precomputed once */
	public setFlashes(flashes: LightningFlash[]): void {
		const sorted = flashes.filter(f => Number.isFinite(f.t) && Number.isFinite(f.lat) && Number.isFinite(f.lon)).sort((a, b) => a.t - b.t)
		this.times = new Float64Array(sorted.length)
		this.positions = new Float32Array(sorted.length * 3)
		this.strengths = new Float32Array(sorted.length)
		sorted.forEach((f, i) => {
			this.times[i] = f.t
			const p = new geographic(f.lon * trig.rad, f.lat * trig.rad, cAltitudeKm).cartesian
			this.positions[i * 3] = p.x
			this.positions[i * 3 + 1] = p.y
			this.positions[i * 3 + 2] = p.z
			const logE = Math.log10(Math.max(f.energy, 1e-18))
			this.strengths[i] = Math.min(1, Math.max(0.2, (logE - cLogEnergyFaint) / (cLogEnergyBright - cLogEnergyFaint)))
		})
		this.builtForMs = Number.NaN
	}

	/** Index of the first flash at or after t (binary search) */
	private lowerBound(t: number): number {
		let lo = 0
		let hi = this.times.length
		while (lo < hi) {
			const mid = (lo + hi) >> 1
			if (this.times[mid] < t) lo = mid + 1
			else hi = mid
		}
		return lo
	}

	/** Rebuild the quad set for the flashes inside the window ending at the scene clock */
	private rebuild(nowMs: number): void {
		this.builtForMs = nowMs
		const start = this.lowerBound(nowMs - cWindowMs)
		const end = this.lowerBound(nowMs + 1)
		let count = end - start
		// Keep the newest when capped: they are the brightest after fading
		const first = count > cMaxFlashesPerFrame ? end - cMaxFlashesPerFrame : start
		count = end - first
		if (count <= 0) {
			this.geometry.update(new Float32Array(0))
			return
		}
		const vertices = new Float32Array(count * 6 * cVertexFloats)
		let dst = 0
		for (let i = first; i < end; i++) {
			const age = (nowMs - this.times[i]) / cWindowMs
			const strength = this.strengths[i] * Math.pow(1 - Math.min(1, Math.max(0, age)), 0.6)
			const x = this.positions[i * 3]
			const y = this.positions[i * 3 + 1]
			const z = this.positions[i * 3 + 2]
			for (let c = 0; c < 6; c++) {
				vertices[dst++] = x
				vertices[dst++] = y
				vertices[dst++] = z
				vertices[dst++] = cQuadCorners[c * 2]
				vertices[dst++] = cQuadCorners[c * 2 + 1]
				vertices[dst++] = strength
			}
		}
		this.geometry.update(vertices)
	}

	public override render(state: RenderState): RenderActionSet[] {
		if (this.times.length === 0) {
			return []
		}
		const nowMs = this.context.clock.clockTime.date.getTime()
		if (nowMs !== this.builtForMs) {
			this.rebuild(nowMs)
		}
		return this.geometry.available ? super.render(state) : []
	}

	public override destroy(): void {
		this.geometry.destroy()
		super.destroy()
	}
}

/** Dynamic quad list: re-uploaded whenever the visible flash set changes */
class LightningGeometry implements Geometry {
	public mode: DrawMode = DrawMode.Triangles
	public position: VertexAttribute
	public corner: VertexAttribute
	public strength: VertexAttribute
	public vertexBuffer: VertexBuffer
	public bounds = new cube(-1e5, 1e5)
	public boundingSphere = new sphere(1e5)
	public available: boolean = false

	constructor(context: EarthBrowserContext) {
		this.position = new VertexAttribute(context, 3)
		this.corner = new VertexAttribute(context, 2)
		this.strength = new VertexAttribute(context, 1)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.corner, this.strength])
	}

	public update(vertices: Float32Array): void {
		if (vertices.length === 0) {
			this.available = false
			return
		}
		this.vertexBuffer.uploadData(vertices)
		this.available = true
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
	}
}

class LightningRenderer extends Renderer {
	private modelViewProjection = new UniformDynamic<matrix>()
	private cameraPosition = new UniformDynamic<vec3>()
	private viewport = new UniformDynamic<vec2>()
	private sizePx = new UniformDynamic<number>()

	constructor(context: EarthBrowserContext, geometry: Geometry) {
		super(context, new ShaderSourceDefinition("lightning"), geometry)
		// Additive over the deck, no depth: the flashes sit just above the
		// surface and must not z-fight the overlay or hide behind terrain
		this.drawState.depthTest = false
		this.drawState.depthWrite = false
		this.drawState.blend = [BlendFactor.SrcAlpha, BlendFactor.One]
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		// Horizon cull in the shader: flashes are earth-fixed, and this node
		// carries no transform of its own, so the camera is used in object space
		this.cameraPosition.value = state.camera.position
		this.viewport.value = new vec2(state.width, state.height)
		this.sizePx.value = cSizePx
	}
}
